import { cssColour, dispose, mountScene, observeScene, openRenderer } from "./renderer.js";
import type { ResourceScope } from "./lifecycle.js";
/**
 * Rendu WebGL du schéma (Three.js).
 *
 * Ce module ne décide d'aucune géométrie : il dessine ce que `schematic.ts`
 * lui donne. C'est délibéré — la géométrie est testable, le rendu ne l'est pas
 * (WebGL n'existe pas dans l'environnement de test). Tout ce qui pourrait être
 * faux est donc du côté testé, et ce fichier reste une traduction mécanique.
 *
 * La règle §3 de CLAUDE.md — trait plein pour une prédiction, points pour une
 * mesure, pointillé pour une référence — gouverne les **séries de données**.
 * Cette vue n'en porte aucune : ni mesure, ni prédiction, ni écart. C'est un
 * dessin d'explication. Les traits y sont tous pleins, et rien ne s'y lit
 * comme un résultat.
 *
 * Three.js est chargé dynamiquement : la page reste utilisable sans lui, et
 * elle reste honnête sans lui — le schéma n'apporte aucune conclusion.
 */

import { boundsCentre, buildSchematic, schematicBounds, schematicPoints } from "./schematic.js";
import type { Schematic, SchematicGeometry, SchematicRequest, Vec3 } from "./schematic.js";
import type { SceneHandle } from "../experiment/types.js";
import { escape, escapeAttribute } from "./format.js";
import { fr } from "../i18n/fr.js";

type ThreeModule = typeof import("three");

const GEOMETRIES: readonly { readonly id: SchematicGeometry; readonly label: string }[] = [
  { id: "spherical", label: fr.schematicSpherical },
  { id: "flat", label: fr.schematicFlat },
];

const DEFAULT_GEOMETRY: SchematicGeometry = "spherical";

/** Direction du regard, depuis le centre de la scène. Trois-quarts, légèrement au-dessus. */
const VIEW_DIRECTION = { x: 0.28, y: 0.3, z: 1 } as const;
const FIELD_OF_VIEW = 38;
/** Marge autour de la scène, pour ne pas la coller aux bords. */
const FRAMING_MARGIN = 1.06;

const GNOMON_RADIUS = 0.014;
const SHADOW_RADIUS = 0.011;
/** Décalage des traits posés sur le sol, pour qu'ils ne s'y enfoncent pas. */
const SURFACE_LIFT = 0.008;

// ── Coquille HTML ────────────────────────────────────────────────────────────

export function renderSchematicShell(): string {
  const buttons = GEOMETRIES.map(({ id, label }) =>
    [
      `<button type="button" class="verlet-toggle" data-geometry="${escapeAttribute(id)}"`,
      ` aria-pressed="${id === DEFAULT_GEOMETRY ? "true" : "false"}">${escape(label)}</button>`,
    ].join(""),
  ).join("");

  return [
    `<figure class="verlet-scene">`,
    `<div class="verlet-toggles" role="group"`,
    ` aria-label="${escapeAttribute(fr.schematicToggleLabel)}">${buttons}</div>`,
    `<div class="verlet-scene__stage" data-scene-stage role="img"`,
    ` aria-label="${escapeAttribute(fr.schematicAlt(DEFAULT_GEOMETRY))}">`,
    `<p class="verlet-scene__fallback">${escape(fr.schematicLoading)}</p>`,
    `</div>`,
    `<figcaption class="verlet-caveat">${escape(fr.schematicCaveat)}</figcaption>`,
    `</figure>`,
  ].join("");
}

// ── Montage ──────────────────────────────────────────────────────────────────

/**
 * Ce que la vue réclame pour dessiner : la commande correspondant à la
 * géométrie choisie.
 *
 * Elle vient de l'expérience, jamais d'ici. Ce module ne connaît aucun modèle,
 * aucune éphéméride et aucun état — il traduit une commande en triangles. Le
 * rapport ombre / hauteur qu'il reçoit est **la prédiction du modèle
 * sélectionné**, et c'est ce qui fait de ce dessin un second rendu du chiffre
 * plutôt qu'une illustration.
 */
export type SchematicSource = (geometry: SchematicGeometry) => SchematicRequest;

/**
 * Attache la vue 3D et rend de quoi la redessiner puis la retirer.
 *
 * Ne rejette jamais : le schéma est un second rendu de ce que la page dit déjà
 * en chiffres, son absence ne doit rien casser — elle est simplement annoncée.
 * Les fonctions rendues sont sans effet tant que le montage n'a pas abouti.
 *
 * `detach` n'est pas une politesse. La vue observe la taille de son conteneur
 * et le thème du système ; changer d'expérience sans la démonter laisserait ces
 * écoutes vivantes, à redessiner un canvas retiré du document — et à en
 * accumuler une par visite.
 */
export function mountSchematic(root: ParentNode, source: SchematicSource): SceneHandle {
  return mountScene(root, "[data-scene-stage]", fr.schematicUnavailable, (scope) => attach(root, source, scope));
}

async function attach(
  root: ParentNode,
  source: SchematicSource,
  scope: ResourceScope,
): Promise<{ readonly redraw: () => void } | undefined> {
  const stage = root.querySelector<HTMLElement>("[data-scene-stage]");
  if (stage === null) return;

  const opened = await openRenderer(stage, scope);
  if (!opened) return;
  const { three, renderer } = opened;

  const scene = new three.Scene();
  scope.own(() => dispose(scene));
  const camera = new three.PerspectiveCamera(FIELD_OF_VIEW, 1, 0.1, 100);
  scene.add(camera);

  let content = new three.Group();
  let geometry: SchematicGeometry = DEFAULT_GEOMETRY;
  scene.add(content);

  const show = (): void => {
    const schematic = buildSchematic(source(geometry));

    scene.remove(content);
    dispose(content);
    content = build(three, schematic);
    scene.add(content);
    stage.setAttribute("aria-label", fr.schematicAlt(geometry));

    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (width === 0 || height === 0) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    frame(three, camera, schematic, camera.aspect);
    renderer.render(scene, camera);
  };

  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-geometry]")) {
    const onGeometry = (): void => {
      const chosen = button.dataset["geometry"];
      if (chosen !== "spherical" && chosen !== "flat") return;
      for (const other of root.querySelectorAll<HTMLButtonElement>("[data-geometry]")) {
        other.setAttribute("aria-pressed", other === button ? "true" : "false");
      }
      geometry = chosen;
      show();
    };
    button.addEventListener("click", onGeometry);
    scope.own(() => button.removeEventListener("click", onGeometry));
  }

  observeScene(scope, stage, show, show);

  scope.own(() => {
    scene.remove(content);
    dispose(content);
  });

  return { redraw: show };
}

// ── Construction de la scène ─────────────────────────────────────────────────

function vector(three: ThreeModule, v: Vec3): InstanceType<ThreeModule["Vector3"]> {
  return new three.Vector3(v.x, v.y, v.z);
}

/** Un trait épais entre deux points : un cylindre orienté. */
function segment(
  three: ThreeModule,
  from: Vec3,
  to: Vec3,
  radius: number,
  material: InstanceType<ThreeModule["MeshBasicMaterial"]>,
  lift: Vec3 = { x: 0, y: 0, z: 0 },
): InstanceType<ThreeModule["Mesh"]> | undefined {
  const start = vector(three, from).add(vector(three, lift));
  const end = vector(three, to).add(vector(three, lift));
  const axis = end.clone().sub(start);
  const length = axis.length();
  if (length < 1e-9) return undefined;

  const mesh = new three.Mesh(new three.CylinderGeometry(radius, radius, length, 10), material);
  mesh.position.copy(start).add(axis.clone().multiplyScalar(0.5));
  mesh.quaternion.setFromUnitVectors(new three.Vector3(0, 1, 0), axis.clone().normalize());
  return mesh;
}

function build(
  three: ThreeModule,
  schematic: Schematic,
): InstanceType<ThreeModule["Group"]> {
  const group = new three.Group();

  const ink = new three.Color(cssColour("--ink", "#1b1a17"));
  const inkSoft = new three.Color(cssColour("--ink-soft", "#5c584f"));
  const paper = new three.Color(cssColour("--paper", "#faf8f4"));
  const rule = new three.Color(cssColour("--rule", "#d8d2c6"));
  const accent = new three.Color(cssColour("--accent", "#b4622a"));

  const solid = new three.MeshBasicMaterial({ color: paper });
  const mesh = new three.LineBasicMaterial({ color: rule });
  const inkMaterial = new three.MeshBasicMaterial({ color: ink });
  // L'ombre est plus pâle et plus fine que le bâton : sans ça, les deux ne font
  // qu'un « V » d'un seul tenant et on ne sait plus lequel est l'objet.
  const shadowMaterial = new three.MeshBasicMaterial({ color: inkSoft });
  const accentMaterial = new three.MeshBasicMaterial({ color: accent });
  const rayMaterial = new three.LineBasicMaterial({ color: accent, transparent: true, opacity: 0.75 });

  // ── Le sol ─────────────────────────────────────────────────────────────
  if (schematic.ground.kind === "sphere") {
    const { radius } = schematic.ground;
    // Une sphère pleine pour occulter ce qui passe derrière, un quadrillage
    // par-dessus pour que la courbure se lise. Le quadrillage est fait de
    // parallèles et de méridiens, pas d'un `WireframeGeometry` : celui-ci
    // ajoute les diagonales de triangulation, qui affleuraient la sphère pleine
    // et la piquaient d'artefacts en étoile.
    group.add(new three.Mesh(new three.SphereGeometry(radius * 0.985, 64, 48), solid));
    for (const points of graticule(radius)) {
      group.add(
        new three.Line(new three.BufferGeometry().setFromPoints(points.map((p) => vector(three, p))), mesh),
      );
    }
  } else {
    const { radius } = schematic.ground;
    const disc = new three.Mesh(
      new three.CircleGeometry(radius, 72),
      new three.MeshBasicMaterial({ color: paper, side: three.DoubleSide }),
    );
    disc.rotation.x = -Math.PI / 2;
    group.add(disc);

    for (const points of discGraticule(radius)) {
      const line = new three.Line(
        new three.BufferGeometry().setFromPoints(points.map((p) => vector(three, p))),
        mesh,
      );
      // Juste au-dessus du plan, sinon le trait s'y confond par z-fighting.
      line.position.y = SURFACE_LIFT / 2;
      group.add(line);
    }
  }

  // ── Les rayons ─────────────────────────────────────────────────────────
  for (const ray of schematic.rays) {
    const geometry = new three.BufferGeometry().setFromPoints([
      vector(three, ray.from),
      vector(three, ray.to),
    ]);
    group.add(new three.Line(geometry, rayMaterial));
  }

  // ── Le Soleil ──────────────────────────────────────────────────────────
  const sun = new three.Mesh(
    new three.SphereGeometry(schematic.sun.radius, 24, 16),
    accentMaterial,
  );
  sun.position.copy(vector(three, schematic.sun.position));
  group.add(sun);

  if (schematic.sun.local) {
    // La hauteur du Soleil est un paramètre du modèle plat : elle mérite d'être visible.
    const foot = { x: schematic.sun.position.x, y: 0, z: schematic.sun.position.z };
    const height = segment(three, foot, schematic.sun.position, 0.003, new three.MeshBasicMaterial({ color: rule }));
    if (height !== undefined) group.add(height);
  }

  // ── Les gnomons et leurs ombres ────────────────────────────────────────
  for (const gnomon of schematic.gnomons) {
    const stick = segment(three, gnomon.base, gnomon.top, GNOMON_RADIUS, inkMaterial);
    if (stick !== undefined) group.add(stick);

    // Pas d'ombre prédite, pas d'ombre dessinée. On n'en invente pas une pour
    // remplir le vide : c'est le Soleil sous l'horizon, et ça se voit.
    if (gnomon.shadowEnd === undefined) continue;

    const lift =
      schematic.ground.kind === "sphere"
        ? scaled(gnomon.base, SURFACE_LIFT / lengthOf(gnomon.base))
        : { x: 0, y: SURFACE_LIFT, z: 0 };
    const shadow = segment(three, gnomon.base, gnomon.shadowEnd, SHADOW_RADIUS, shadowMaterial, lift);
    if (shadow !== undefined) group.add(shadow);
  }

  return group;
}

const MERIDIANS = 12;
const PARALLELS = 7;
const ARC_STEPS = 64;

/** Parallèles et méridiens d'un globe, en polylignes. */
function graticule(radius: number): readonly (readonly Vec3[])[] {
  const lines: Vec3[][] = [];

  for (let index = 0; index < MERIDIANS; index += 1) {
    const longitude = (Math.PI * index) / MERIDIANS;
    lines.push(
      Array.from({ length: ARC_STEPS + 1 }, (_, step) => {
        const angle = -Math.PI / 2 + (Math.PI * step) / ARC_STEPS;
        return {
          x: radius * Math.cos(angle) * Math.cos(longitude),
          y: radius * Math.sin(angle),
          z: radius * Math.cos(angle) * Math.sin(longitude),
        };
      }),
    );
  }

  for (let index = 1; index <= PARALLELS; index += 1) {
    const latitude = -Math.PI / 2 + (Math.PI * index) / (PARALLELS + 1);
    const ring = radius * Math.cos(latitude);
    const height = radius * Math.sin(latitude);
    lines.push(
      Array.from({ length: ARC_STEPS + 1 }, (_, step) => {
        const angle = (2 * Math.PI * step) / ARC_STEPS;
        return { x: ring * Math.cos(angle), y: height, z: ring * Math.sin(angle) };
      }),
    );
  }

  return lines;
}

/**
 * Quadrillage de la carte plate : cercles de colatitude et rayons.
 *
 * Ce n'est pas de la décoration. Rempli en couleur papier sur un fond papier,
 * le disque était invisible — il n'en restait qu'un liseré. Et ce quadrillage
 * est l'exact analogue des parallèles et méridiens du globe : c'est la
 * structure que le modèle plat postule, celle où la distance au centre est
 * proportionnelle à la colatitude.
 */
function discGraticule(radius: number): readonly (readonly Vec3[])[] {
  const lines: Vec3[][] = [];

  for (let index = 1; index <= PARALLELS + 1; index += 1) {
    const ring = (radius * index) / (PARALLELS + 1);
    lines.push(
      Array.from({ length: ARC_STEPS + 1 }, (_, step) => {
        const angle = (2 * Math.PI * step) / ARC_STEPS;
        return { x: ring * Math.cos(angle), y: 0, z: ring * Math.sin(angle) };
      }),
    );
  }

  for (let index = 0; index < MERIDIANS; index += 1) {
    const angle = (Math.PI * index) / MERIDIANS;
    lines.push([
      { x: -radius * Math.cos(angle), y: 0, z: -radius * Math.sin(angle) },
      { x: radius * Math.cos(angle), y: 0, z: radius * Math.sin(angle) },
    ]);
  }

  return lines;
}

function lengthOf(v: Vec3): number {
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1;
}

function scaled(v: Vec3, k: number): Vec3 {
  return { x: v.x * k, y: v.y * k, z: v.z * k };
}

// ── Cadrage ──────────────────────────────────────────────────────────────────

/**
 * Cadre la scène entière depuis une direction fixe.
 *
 * Le recul est calculé, pas réglé à la main : on cherche la plus petite
 * distance pour laquelle **chaque point réellement occupé** tient dans le
 * tronc de vision. Deux approximations ont été essayées avant celle-ci et
 * échouaient de la même façon, en majorant la scène : une sphère englobante,
 * puis les huit sommets de la boîte englobante — dont les coins, autour d'un
 * globe, ne contiennent rien. La caméra reculait alors jusqu'à ne remplir
 * qu'un quart de la largeur.
 */
function frame(
  three: ThreeModule,
  camera: InstanceType<ThreeModule["PerspectiveCamera"]>,
  schematic: Schematic,
  aspect: number,
): void {
  const centre = vector(three, boundsCentre(schematicBounds(schematic)));

  const direction = new three.Vector3(
    VIEW_DIRECTION.x,
    VIEW_DIRECTION.y,
    VIEW_DIRECTION.z,
  ).normalize();
  const forward = direction.clone().negate();
  const right = new three.Vector3()
    .crossVectors(forward, new three.Vector3(0, 1, 0))
    .normalize();
  const up = new three.Vector3().crossVectors(right, forward);

  const halfVertical = ((FIELD_OF_VIEW / 2) * Math.PI) / 180;
  const halfHorizontal = Math.atan(Math.tan(halfVertical) * aspect);
  const tanHorizontal = Math.tan(halfHorizontal);
  const tanVertical = Math.tan(halfVertical);

  let distance = 0;
  let depth = 0;
  for (const point of schematicPoints(schematic)) {
    const offset = vector(three, point).sub(centre);
    const along = offset.dot(direction);
    distance = Math.max(
      distance,
      along + (Math.abs(offset.dot(right)) * FRAMING_MARGIN) / tanHorizontal,
      along + (Math.abs(offset.dot(up)) * FRAMING_MARGIN) / tanVertical,
    );
    depth = Math.max(depth, Math.abs(along));
  }

  camera.position.copy(centre).add(direction.multiplyScalar(distance));
  camera.lookAt(centre);
  camera.near = Math.max(0.01, distance - depth * 1.5);
  camera.far = distance + depth * 1.5;
  camera.updateProjectionMatrix();
}

// ── Nettoyage ────────────────────────────────────────────────────────────────
